import React, { useState } from "react";
import { Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";

const initial = {
  name: "", age: 50, gender: "M",
  surgery_type: "", surgery_date: new Date().toISOString().slice(0, 10),
  contact_phone: "", notes: "", care_plan_duration: 20,
};

export function AddPatientDialog({ specialty, onCreated, triggerLabel = "Add patient" }) {
  const { hospCode } = useBranding();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surgery_type) { toast.error("Name and surgery type are required"); return; }
    setSaving(true);
    try {
      const p = await api.createPatient(hospCode, { ...form, age: Number(form.age), specialty });
      toast.success(`Patient ${p.name} added`, { description: `${form.care_plan_duration}-day plan generated` });
      setOpen(false); setForm(initial);
      onCreated && onCreated(p);
    } catch { toast.error("Failed to add patient"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-patient-trigger"
          className="rounded-full h-9 px-4 text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New patient</DialogTitle>
          <DialogDescription>Generates an initial AI-assisted care plan for the surgeon to review.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="patient-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="age">Age</Label>
            <Input id="age" data-testid="patient-age-input" type="number" min={1} max={120}
              value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
          </div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger data-testid="patient-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="M">Male</SelectItem>
                <SelectItem value="F">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label htmlFor="surgery_type">Surgery type</Label>
            <Input id="surgery_type" data-testid="patient-surgery-type-input" value={form.surgery_type}
              onChange={(e) => setForm({ ...form, surgery_type: e.target.value })}
              placeholder="e.g. Total Knee Replacement" required />
          </div>
          <div>
            <Label htmlFor="surgery_date">Surgery date</Label>
            <Input id="surgery_date" data-testid="patient-surgery-date-input" type="date"
              value={form.surgery_date} onChange={(e) => setForm({ ...form, surgery_date: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="contact_phone">Contact phone</Label>
            <Input id="contact_phone" data-testid="patient-phone-input" value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label>Care plan duration</Label>
              <span className="text-xs font-semibold text-foreground tabular-nums">{form.care_plan_duration} days</span>
            </div>
            <Slider
              min={12} max={30} step={1}
              value={[form.care_plan_duration]}
              onValueChange={([v]) => setForm({ ...form, care_plan_duration: v })}
              data-testid="patient-duration-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">
              <span>12 days</span><span>20 default</span><span>30 days</span>
            </div>
          </div>
          <div className="col-span-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" data-testid="patient-notes-input" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter className="col-span-2 gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}
              data-testid="patient-submit-btn"
              className="rounded-full">
              {saving ? "Saving…" : "Create patient"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
